(ns hai.ui.components.collection.facets
  "Shared facet-sidebar chrome for collection pages (the notes wall, the tasks
   board): the sidebar's look and the collapsible `facet-group`.

   The sidebar is palette-neutral. A page gives it its colours by inheriting it
   and setting these custom properties:

   - `--facet-border`    the sidebar's outline
   - `--facet-muted`     headings, counts, secondary controls
   - `--facet-ink`       option text
   - `--facet-tint`      hover / selected fill, loading bars
   - `--facet-scrollbar` the scrollbar thumb

   e.g. `(o/defstyled sidebar facets/sidebar {:--facet-ink t/--note-kraft ...})`."
  (:require
   [hai.ui.icons :as icons]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private chevron-icon (icons/mui-icon :outlined :chevron_left))

;; Garden keyframes are evaluated at macro time, so garden.stylesheet needs no
;; cljs require here.
#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(o/defrules skeleton-pulse
  (garden.stylesheet/at-keyframes
   :hai-facet-skeleton-pulse
   [:from {:opacity "1"}]
   ["50%" {:opacity "0.7"}]
   [:to {:opacity "1"}]))

(o/defstyled sidebar :aside
  {:width "240px"
   ;; Pins just below the page's sticky header.
   :position "sticky" :top "76px"
   :align-self "start"
   :max-height "calc(100vh - 200px)"
   :overflow-y "auto"
   :display "flex" :flex-direction "column" :gap "12px"
   :padding "0 12px 14px"
   :border-width "1px" :border-style "solid" :border-color "var(--facet-border)"
   :border-radius "10px"
   :background t/--colour-snow
   :scrollbar-width "thin"
   :scrollbar-color "var(--facet-scrollbar) transparent"}
  [:.facet-group {:display "flex" :flex-direction "column"}]
  [:.facet-heading
   ;; Sticky within its own `.facet-group`: the heading stays pinned to the top
   ;; of the scroll area until the group's last option scrolls past, then the
   ;; next group's heading pushes it out. Needs an opaque background and a
   ;; z-index so options scroll UNDER it.
   {:position "sticky" :top "0" :z-index "1"
    :display "flex" :align-items "center" :gap "6px"
    :width "100%"
    :padding "12px 0 6px"
    :border "none"
    :background t/--colour-snow
    :font-family "inherit" :font-size "11px" :font-weight "700"
    :letter-spacing "0.06em" :text-transform "uppercase"
    :text-align "left"
    :color "var(--facet-muted)"
    :cursor "pointer"}
   [:svg {:width "13px" :height "13px" :fill "currentColor"}]
   ;; On the far right: ∨ while open, pointing back at the heading (<) while
   ;; collapsed.
   [:.facet-chevron
    {:display "inline-flex" :align-items "center"
     :margin-left "auto"}
    [:svg {:width "16px" :height "16px"
           :transition "transform 0.15s ease"}]]]
  [:.facet-group.open
   [:.facet-heading [:.facet-chevron [:svg {:transform "rotate(-90deg)"}]]]]
  [:.facet-options {:display "flex" :flex-direction "column" :gap "2px"}]
  ;; Placeholder bar shown while the first facet load is in flight, so an
  ;; unloaded sidebar reads as "loading" rather than "no filters".
  [:.facet-skeleton
   {:height "13px" :border-radius "7px"
    :background "var(--facet-tint)"
    :animation "hai-facet-skeleton-pulse 1.4s ease-in-out infinite"}]
  [:.facet-option
   {:display "flex" :align-items "center" :gap "8px"
    :width "100%" :text-align "left"
    :padding "3px 8px" :border "none" :border-radius "7px"
    :background "transparent"
    :font-family "inherit" :font-size "13px" :font-weight "500"
    :color "var(--facet-ink)" :cursor "pointer"
    :min-width "0"}
   [:&:hover {:background "var(--facet-tint)"}]
   [:&.selected {:background "var(--facet-tint)" :font-weight "700"}]
   [:&.zero {:opacity "0.45"}]
   [:.opt-label
    {:flex "1" :min-width "0"
     :overflow "hidden" :text-overflow "ellipsis" :white-space "nowrap"}]
   [:.opt-count {:flex "0 0 auto" :font-size "12px" :font-weight "500"
                 :color "var(--facet-muted)"}]]
  ;; Nested rows (e.g. the notes wall's domains) put an expand chevron beside
  ;; the selectable row, with their children indented beneath.
  [:.domain-row {:display "flex" :align-items "center" :min-width "0"}
   [:.facet-option {:flex "1"}]]
  [:.chevron
   {:display "inline-flex" :align-items "center" :justify-content "center"
    :width "20px" :height "20px" :flex "0 0 auto"
    :border "none" :border-radius "6px"
    :background "transparent" :padding "0"
    :color "var(--facet-muted)" :cursor "pointer"}
   [:&:hover {:background "var(--facet-tint)"}]
   [:svg {:width "15px" :height "15px" :fill "currentColor"
          :transition "transform 0.15s ease"}]
   [:&.open [:svg {:transform "rotate(90deg)"}]]]
  [:.child {:padding-left "26px"}]
  [:.clear-all
   {:align-self "flex-start"
    :border "none" :background "transparent" :padding "0 8px"
    :font-family "inherit" :font-size "12px" :font-weight "600"
    :color "var(--facet-muted)" :cursor "pointer"}
   [:&:hover {:color "var(--facet-ink)" :text-decoration "underline"}]]
  [:at-media {:max-width "768px"}
   {:position "static" :width "100%" :max-height "none"}])

(defn facet-group
  "A collapsible facet group headed by `icon` + `heading`, with `body` (the
   group's options) shown while open. Every group starts open; the collapsed
   state is local to the mount. Styled by `sidebar`."
  [{:keys [icon heading]} body]
  (r/with-let [!open? (r/atom true)]
    (let [open? @!open?]
      [:section.facet-group {:class (when open? "open")}
       [:button.facet-heading
        {:type          "button"
         :aria-expanded open?
         :on-click      #(swap! !open? not)}
        [icon] heading
        [:span.facet-chevron [chevron-icon]]]
       (when open? body)])))
